import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { cp } from "../../config";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { AreaFooter } from "../areaFooter/AreaFooter";
import AreaHeader from "../areaHeader/AreaHeader";

import { ReactComponent as TeacherLogo } from "../../assets/img/phase6-teachers.svg";
import { ReactComponent as Phase6Logo } from "../../assets/img/p6-logo.svg";
import { Header, IFrameHeaderPlaceholder, PageWrapper } from "./StyledComponents";
import { creationFlowColors, getCreationMode } from "../../constants/CreationFlowConstants";

export enum LogoVariants {
    TEACHER = "TEACHER",
    PHASE6 = "PHASE6",
}

interface FullHeaderProps {
    isAreaHeader: true;
    noPadding?: boolean;
    id?: string;
    children?: ReactNode;
}

interface MinimisedHeaderProps {
    isAreaHeader?: false;
    id?: string;
    isFullWidth?: boolean;
    hasInternalScroll?: boolean;
    hasPinnedFooter?: boolean; // requires the child to scroll in its own element, the wrapper stops scrolling as a page.
    hasGrayBackground?: boolean;
    noPadding?: boolean;
    // lays the content out as a flex column so a child can grow to fill the remaining
    // viewport height (e.g. to pin a sticky CTA footer to the bottom on short pages)
    hasFullHeightContent?: boolean;
    onLogoClick?: () => void;
    logoVariant?: LogoVariants;
    /**
     * Rendered as a sibling after <main>: a <footer> inside <main> would lose its contentinfo
     * landmark, so pages pass it here instead of in children.
     */
    footer?: ReactNode;
    children?: ReactNode;
}

type HeaderWrapperProps = FullHeaderProps | MinimisedHeaderProps;

const ContentWithHeader = (props: HeaderWrapperProps) => {
    const { isIframeMode, userInfo, teacherInfo, avatarUrl, isAnonFromTeacherArea, creatingMode, setCreatingMode } =
        useGeneralContext();

    const { pathname } = useLocation();

    const creationColors = creationFlowColors[getCreationMode(creatingMode)];

    // Clear creation color when navigating outside the creation flow so it does not leak
    // into other pages (e.g. browser back/forward, 401 redirects to /auth, future exit paths).
    useEffect(() => {
        if (!pathname.startsWith("/create") && creatingMode) {
            setCreatingMode("");
        }
    }, [pathname, creatingMode, setCreatingMode]);

    // Set CSS vars on document root so portaled elements (e.g. react-modal) can access them
    useEffect(() => {
        if (!creatingMode) return;

        document.documentElement.style.setProperty("--creation-primary-color", creationColors.primary);
        document.documentElement.style.setProperty("--creation-primary-color-light", creationColors.primaryLight);
        document.documentElement.style.setProperty(
            "--creation-primary-color-light-pastel",
            creationColors.primaryLightPastel
        );

        return () => {
            document.documentElement.style.removeProperty("--creation-primary-color");
            document.documentElement.style.removeProperty("--creation-primary-color-light");
            document.documentElement.style.removeProperty("--creation-primary-color-light-pastel");
        };
    }, [creatingMode, creationColors]);

    const hideHeader = isIframeMode && !isAnonFromTeacherArea;
    const onLogoClick = !props.isAreaHeader ? props.onLogoClick : undefined;
    const logoVariant: LogoVariants = (!props.isAreaHeader && props.logoVariant) || LogoVariants.TEACHER;
    const Logo = logoVariant === LogoVariants.PHASE6 ? Phase6Logo : TeacherLogo;
    const hasFullHeightContent = !props.isAreaHeader && props.hasFullHeightContent;

    // Minimised header shows only the logo, used when the user is in a focused flow
    // and should not be distracted or accidentally navigate away via the full navigation header.
    const renderMinimisedHeader = () => {
        if (hideHeader) {
            return <IFrameHeaderPlaceholder />;
        }
        return (
            <Header id={"aea-header"}>
                <Logo
                    className={`h-[90%] w-auto ${onLogoClick ? "cursor-pointer" : ""}`}
                    onClick={onLogoClick}
                />
            </Header>
        );
    };

    if (props.isAreaHeader) {
        return (
            <PageWrapper id={props.id}>
                <AreaHeader
                    basePath={`${cp.cfg.REACT_APP_P6_BASE_WEBSITE}schule`}
                    currentPath="/assistent"
                    userInfo={userInfo}
                    teacherInfo={teacherInfo ?? undefined}
                    avatarUrl={avatarUrl}
                />
                <div className="h-[var(--size-header-m)] w-full" />
                <main className={`flex flex-1 justify-center ${props.noPadding ? "" : "pb-m"}`}>{props.children}</main>
                <AreaFooter />
            </PageWrapper>
        );
    }

    const getContentClassName = (): string => {
        if (props.hasInternalScroll) {
            return "h-[calc(100vh-60px)] overflow-hidden";
        }

        // With a footer sibling, <main> grows to push it to the viewport bottom (like the
        // area-header branch); a viewport-height min-height would shove the footer below the fold.
        // When the footer is pinned, min-h-[0] lets <main> shrink below its content instead, so the
        // child scroller absorbs the overflow rather than the footer being pushed off-screen.
        const pinnedFooterClass = props.hasPinnedFooter ? " min-h-[0]" : "";
        const heightClass = props.footer ? `flex-1 flex flex-col${pinnedFooterClass}` : "min-h-[calc(100vh-60px)]";

        if (props.noPadding) {
            return `${heightClass} ${hasFullHeightContent ? "flex flex-col" : ""}`;
        }

        const topPadding = hideHeader ? "pt-0" : "pt-s";
        const bottomLayout = hasFullHeightContent ? "pb-no flex flex-col" : "pb-xl";

        return `${heightClass} px-s ${topPadding} ${bottomLayout}`;
    };

    return (
        <PageWrapper
            id={props.id}
            // "fill" caps the wrapper at viewport height for the true embedded-iframe case (hidden header).
            // Gate it on hideHeader — not isIframeMode — so anonymous-from-teacher-area users, who see the
            // full header + close bar and scroll long content, let the wrapper grow (like logged-in users).
            // Otherwise the wrapper is capped, its content overflows, and the sticky close bar releases early.
            className={`${props.hasGrayBackground ? "gray" : ""} ${hideHeader ? "fill" : ""} ${
                props.hasPinnedFooter ? "pinned-footer" : ""
            }`}
        >
            {renderMinimisedHeader()}
            <main
                className={`mx-auto w-full ${getContentClassName()}`}
                style={
                    !props.isFullWidth ? { maxWidth: `${process.env.REACT_APP_UPPER_GRID_STEP_BORDER}px` } : undefined
                }
            >
                {props.children}
            </main>
            {props.footer}
        </PageWrapper>
    );
};

export default ContentWithHeader;
